import { useContext, useMemo } from 'react'

import { ProductCombinedListingContext } from '@lib/product/combined-listing/context'
import {
  getPassepartoutHoleSizeFromArtworkSize,
  getSizeDimensionsFromString,
  getSizeString,
} from '@lib/size'
import { StringsContext } from '@lib/strings-context'

import ProductDimensions from './product-dimensions'

const CombinedListingFrameDimensions = () => {
  const {
    combinedListingProducts,
    combinedListingFrameSize,
    combinedListingArtworkSize,
  } = useContext(ProductCombinedListingContext)
  const strings = useContext(StringsContext)

  const profile = useMemo(() => {
    const frameProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'frame',
    )

    return frameProduct?.combinedListingFrameBuilderProfile?.profile
  }, [combinedListingProducts])

  const artworkSizeString = useMemo(() => {
    if (combinedListingArtworkSize) {
      const [artworkWidth, artworkHeight] = getSizeDimensionsFromString(
        combinedListingArtworkSize,
      )
      const artworkSize = getSizeString(artworkWidth, artworkHeight, {
        spacingAroundSeparator: true,
      })

      return `${artworkSize} ${strings.productSizeSuffix ?? ''}`
    }

    if (combinedListingFrameSize) {
      const [frameWidth, frameHeight] = getSizeDimensionsFromString(
        combinedListingFrameSize,
      )
      const frameSize = getSizeString(frameWidth, frameHeight, {
        spacingAroundSeparator: true,
      })

      return `${frameSize} ${strings.productSizeSuffix ?? ''}`
    }
  }, [combinedListingArtworkSize, combinedListingFrameSize, strings])

  const innerFrameSizeString = useMemo(() => {
    const frameProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'frame',
    )

    if (!combinedListingFrameSize || !frameProduct) {
      return
    }

    // Sizes are stored in millimeters, shown in centimeters
    const frameRabbetWidth = (profile?.rabbetWidth ?? 0) / 10

    // Subtract rabbet width from frame size
    const [frameWidth, frameHeight] = getSizeDimensionsFromString(
      combinedListingFrameSize,
    )
    const innerFrameWidth = frameWidth - frameRabbetWidth * 2
    const innerFrameHeight = frameHeight - frameRabbetWidth * 2
    const innerFrameSize = getSizeString(innerFrameWidth, innerFrameHeight, {
      spacingAroundSeparator: true,
    })

    return `${innerFrameSize} ${strings.productSizeSuffix ?? ''}`
  }, [combinedListingProducts, combinedListingFrameSize, profile, strings])

  const outerFrameSizeString = useMemo(() => {
    if (!innerFrameSizeString) {
      return
    }

    // Sizes are stored in millimeters, shown in centimeters
    const frameFaceWidth = (profile?.width ?? 0) / 10

    // Add face width to inner frame size
    const [innerFrameWidth, innerFrameHeight] =
      getSizeDimensionsFromString(innerFrameSizeString)
    const outerFrameWidth = innerFrameWidth + frameFaceWidth * 2
    const outerFrameHeight = innerFrameHeight + frameFaceWidth * 2
    const outerFrameSize = getSizeString(outerFrameWidth, outerFrameHeight, {
      spacingAroundSeparator: true,
    })

    return `${outerFrameSize} ${strings.productSizeSuffix ?? ''}`
  }, [innerFrameSizeString, profile, strings])

  const passepartoutSizeString = useMemo(() => {
    // Check if passepartout product is selected
    const passepartoutProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'passepartout',
    )

    if (!passepartoutProduct || !combinedListingFrameSize) {
      return
    }

    const [frameWidth, frameHeight] = getSizeDimensionsFromString(
      combinedListingFrameSize,
    )
    const frameSize = getSizeString(frameWidth, frameHeight, {
      spacingAroundSeparator: true,
    })

    return `${frameSize} ${strings.productSizeSuffix ?? ''}`
  }, [combinedListingFrameSize, combinedListingProducts, strings])

  const passepartoutHoleSizeString = useMemo(() => {
    // Check if passepartout product is selected
    const passepartoutProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'passepartout',
    )

    if (!passepartoutProduct || !combinedListingArtworkSize) {
      return
    }

    // Calculate passepartout cut-out size
    const [artworkWidth, artworkHeight] = getSizeDimensionsFromString(
      combinedListingArtworkSize,
    )
    const [passepartoutHoleWidth, passepartoutHoleHeight] =
      getPassepartoutHoleSizeFromArtworkSize(artworkWidth, artworkHeight)
    const passepartoutHoleSize = getSizeString(
      passepartoutHoleWidth,
      passepartoutHoleHeight,
      {
        spacingAroundSeparator: true,
      },
    )

    return `${passepartoutHoleSize} ${strings.productSizeSuffix ?? ''}`
  }, [combinedListingArtworkSize, combinedListingProducts, strings])

  return (
    <ProductDimensions
      title={strings.productFrameDimensionsHeading}
      artworkSizeString={artworkSizeString}
      innerFrameSizeString={innerFrameSizeString}
      outerFrameSizeString={outerFrameSizeString}
      passepartoutSizeString={passepartoutSizeString}
      passepartoutHoleSizeString={passepartoutHoleSizeString}
    />
  )
}

export default CombinedListingFrameDimensions
